import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { IManagableObject } from "../../../admin-panel/IManagableObject";
import { CompaniesService } from "../../../../services/companies.service";
import { ContainersService } from 'app/services/containers.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-group-filter',
  templateUrl: './group-filter.component.html',
  styleUrls: ['./group-filter.component.css'],
  standalone: false,
})
export class GroupFilterComponent implements AfterViewInit, OnInit, OnDestroy {

  @Input()
  public groups: IManagableObject[] = [];

  @Input()
  public isEditable: boolean = true;

  @Input()
  public canAddGroup: boolean = true;

  @Input()
  public canEditGroup: (group: IManagableObject) => boolean;

  @Input()
  public canDeleteGroup: (group: IManagableObject) => boolean;

  public groupNameFilter: string;
  public isOpen: boolean = false;
  private companyNameById = new Map<number, string>();
  private containerNameById = new Map<number, string>();
  private readonly destroy$ = new Subject<void>();

  public allGroupsItem: IManagableObject = new class implements IManagableObject {
    CompanyId: number;
    CanSelect?: boolean;
    Id: number;
    IsChecked: boolean;
    Name: string;
  };
  public selectedGroup: IManagableObject = this.allGroupsItem;


  @Output() onAddGroup: EventEmitter<any> = new EventEmitter();
  @Output() onEditGroup: EventEmitter<any> = new EventEmitter();
  @Output() onDeleteGroup: EventEmitter<any> = new EventEmitter();
  @Output() onSelectGroup: EventEmitter<any> = new EventEmitter();

  public onTermChange(term: Event) {
    const input = term.target as HTMLInputElement;
    this.groupNameFilter = input.value;
  }

  constructor(
    private translate: TranslateService,
    private elementRef: ElementRef,
    private companiesService: CompaniesService,
    private containersService: ContainersService
  ) { }

  ngOnInit() {
    this.allGroupsItem.Name = this.translate.instant("All");
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() =>
      this.allGroupsItem.Name = this.translate.instant("All"));
    this.allGroupsItem.Id = -1;

    if (!this.hasContainerGroups()) {
      this.companiesService.getCompanies().pipe(takeUntil(this.destroy$)).subscribe(companies => {
        this.companyNameById = new Map(companies.map(company => [company.Id, company.Name] as [number, string]));
      });
    }

    this.containersService.GetContainers().pipe(takeUntil(this.destroy$)).subscribe(containers => {
      this.containerNameById = new Map(containers.map(container => [container.Id, container.Name] as [number, string]));
    });
  }

  ngAfterViewInit() {
    document.dispatchEvent(new Event("readyAgain"));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public openMgmtDropdown(group: any, event: any) {
    this.groups.forEach((grp: any) => {
      if (grp.Id == group.Id) {
        group.isMgmtOpen = true;
        group.mgmtyPos = event.clientY + 10;
        group.mgmtxPos = event.clientX + 10;
      } else {
        grp.isMgmtOpen = false;
      }
    });
  }

  public addGroup() {
    this.closeMgmtDropdowns();
    this.onAddGroup.emit();
  }

  public editGroup(group: IManagableObject) {
    this.closeMgmtDropdowns();
    this.onEditGroup.emit(group);
  }

  public deleteGroup(group: IManagableObject) {
    if (!this.isDeleteAllowed(group)) return;
    let message = this.translate.instant("Delete");
    if (!confirm(message + " " + group.Name + "?")) return;
    if (this.selectedGroup == group)
      this.selectGroup(this.allGroupsItem);
    this.closeMgmtDropdowns();
    this.onDeleteGroup.emit(group);
  }

  public selectGroup(group: IManagableObject) {
    this.selectedGroup = group;
    this.isOpen = false;
    this.closeMgmtDropdowns();
    this.onSelectGroup.emit(group);
  }

  public toggleDropdown(event: MouseEvent) {
    if ((event.target as HTMLElement)?.closest(".dropcontent")) {
      event.stopPropagation();
      return;
    }

    this.isOpen = !this.isOpen;
    event.stopPropagation();
  }

  public isEditAllowed(group: IManagableObject): boolean {
    return !this.canEditGroup || this.canEditGroup(group);
  }

  public isDeleteAllowed(group: IManagableObject): boolean {
    return !this.canDeleteGroup || this.canDeleteGroup(group);
  }

  public getGroupCompanyName(group: IManagableObject): string {
    const companyId = (group as IManagableObject & { CompanyId?: number })?.CompanyId;
    const containerId = (group as IManagableObject & { ContainerId?: number })?.ContainerId;
    if (containerId)
      return this.containerNameById.get(containerId) ?? "";

    if (!companyId)
      return "";

    return this.companyNameById.get(companyId) ?? "";
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent) {
    if (!this.elementRef?.nativeElement?.contains(event.target)) {
      this.isOpen = false;
      this.closeMgmtDropdowns();
    }
  }

  private closeMgmtDropdowns() {
    this.groups.forEach((grp: any) => grp.isMgmtOpen = false);
  }

  private hasContainerGroups(): boolean {
    return this.groups.some(group => !!(group as IManagableObject & { ContainerId?: number })?.ContainerId);
  }

}

